import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  LayoutDashboard, 
  Bell, 
  Users, 
  TrendingUp, 
  Upload, 
  Phone,
  LogOut,
  Menu,
  X,
  Target,
  Settings,
  SendHorizonal,
  Building2
} from 'lucide-react';
import { useState } from 'react';

const Sidebar = () => {
  const { user, logout, isAdmin, isTeamMember } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isSuperAdmin = user?.role === 'superadmin';

  const superAdminLinks = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/admin/franchises', icon: Building2, label: 'Franchisés' },
    { to: '/admin/all-clients', icon: Users, label: 'Tous les clients' },
    { to: '/admin/market', icon: TrendingUp, label: 'Marché' },
    { to: '/admin/upload', icon: Upload, label: 'Upload PDF' },
    { to: '/admin/settings', icon: Settings, label: 'Paramètres' },
  ];

  const adminLinks = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/admin/pending-alerts', icon: SendHorizonal, label: 'Alertes à envoyer' },
    { to: '/admin/market', icon: TrendingUp, label: 'Marché' },
    { to: '/admin/alerts', icon: Bell, label: 'Alertes' },
    { to: '/admin/clients', icon: Users, label: 'Clients' },
    { to: '/admin/recommendations', icon: Target, label: 'Recommandations' },
    { to: '/admin/callbacks', icon: Phone, label: 'Rappels' },
    ...(!isTeamMember ? [
      { to: '/admin/team', icon: Users, label: 'Mon équipe' },
      { to: '/admin/settings', icon: Settings, label: 'Paramètres' },
    ] : []),
  ];

  const clientLinks = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', end: true },
    { to: '/dashboard/callback', icon: Phone, label: 'Demander un rappel' },
  ];

  const links = isSuperAdmin ? superAdminLinks : isAdmin ? adminLinks : clientLinks;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const NavContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="px-6 pt-7 pb-8">
        <img src="/logo_dark.svg" alt="Place des Energies" className="h-8" />
      </div>

      {/* Nav */}
      <nav className="flex-1 px-4 space-y-0.5">
        {links.map(({ to, icon: Icon, label, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `sidebar-item text-sm ${isActive ? 'sidebar-item-active' : ''}`
            }
          >
            <Icon className="w-[18px] h-[18px]" />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User + Logout */}
      <div className="px-4 pb-6 mt-auto">
        <div className="border-t border-[#BFC6CC]/15 pt-4">
          <div className="px-4 pb-3">
            <p className="text-sm font-medium text-[#191D1E] truncate">{user?.name}</p>
            <p className="text-xs text-[#939BA0] truncate">{user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="sidebar-item text-sm w-full text-[#939BA0] hover:text-red-500"
            data-testid="logout-btn"
          >
            <LogOut className="w-[18px] h-[18px]" />
            <span>Déconnexion</span>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 h-full w-64 bg-white border-r border-[#BFC6CC]/15 flex-col z-50">
        <NavContent />
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-[#BFC6CC]/15 z-50 flex items-center px-4">
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 rounded-xl hover:bg-[#F5F6F9]"
          data-testid="mobile-menu-btn"
        >
          {mobileOpen ? <X className="w-5 h-5 text-[#191D1E]" /> : <Menu className="w-5 h-5 text-[#191D1E]" />}
        </button>
        <img src="/logo_light.png" alt="PDE" className="h-6 ml-3" />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-72 bg-white shadow-xl">
            <NavContent />
          </aside>
        </div>
      )}
    </>
  );
};

export default Sidebar;
